import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {Group, Stack, TextInput} from '@mantine/core';
import React from 'react';

import {assert} from 'Asserts';
import {RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {OBJECTS_CREATABLE_PERMISSION} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import {
  BioentityLinkFieldWithSchemaInterface,
  BioentityLinkFieldWithTagSchema,
  BioentityLinkFieldWithTargetSchema,
  isRequiredLinkSchemaInterface,
  isRequiredLinkTagSchema,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import useSchemaSSIsByIdQuery from 'schemas/util/useSchemaSSIsByIdQuery';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {Optional} from 'util/Types';

const MAX_NAME_LENGTH = 255;

const SELECT_NO_FIELD_DUMMY_OBJECT = {
  displayName: 'None',
};

/**
 * Placeholder object used when no entity link field option has been selected yet.
 * This is not displayed as an option in the dropdown.
 */
export const UNSELECTED_DUMMY_OBJECT = {
  displayName: 'Select an option',
};

/**
 * Represents either an actual entity link field or a placeholder object.
 * Used to handle the dropdown selection state before a real field is chosen.
 */
export type EntityLinkFieldOrDummy =
  | SchemaField
  | typeof SELECT_NO_FIELD_DUMMY_OBJECT
  | typeof UNSELECTED_DUMMY_OBJECT;

/**
 * Type guard to check if the provided value is an actual entity link field
 * (as opposed to a dummy/placeholder object).
 */
export function isSelectedEntityLinkField(
  maybeEntityLinkField: Optional<EntityLinkFieldOrDummy>
): maybeEntityLinkField is BioentityLinkFieldWithTargetSchema {
  return (
    maybeEntityLinkField != null &&
    maybeEntityLinkField !== SELECT_NO_FIELD_DUMMY_OBJECT &&
    maybeEntityLinkField !== UNSELECTED_DUMMY_OBJECT
  );
}

function isSchemaLinkField(
  entityLinkFieldOrDummy?: EntityLinkFieldOrDummy
): entityLinkFieldOrDummy is BioentityLinkFieldWithTagSchema {
  return (
    isSelectedEntityLinkField(entityLinkFieldOrDummy) &&
    isRequiredLinkTagSchema(entityLinkFieldOrDummy.requiredLink)
  );
}

function isSchemaInterfaceLinkField(
  entityLinkFieldOrDummy: EntityLinkFieldOrDummy
): entityLinkFieldOrDummy is BioentityLinkFieldWithSchemaInterface {
  return (
    isSelectedEntityLinkField(entityLinkFieldOrDummy) &&
    isRequiredLinkSchemaInterface(entityLinkFieldOrDummy.requiredLink)
  );
}

/**
 * Whether the given schema is compatible with the schema or interface required by the entity link field.
 */
function isSchemaValidForEntityLinkField(
  schemaId: string,
  entityLinkField: BioentityLinkFieldWithTargetSchema,
  interfaceIdsBySchemaId: Record<string, Array<string>>
): boolean {
  const requiredLink = entityLinkField.requiredLink;
  if (isRequiredLinkTagSchema(requiredLink) && requiredLink.tagSchema.id === schemaId) {
    return true;
  }
  if (isRequiredLinkSchemaInterface(requiredLink)) {
    const schemaInterfaces = interfaceIdsBySchemaId[schemaId] ?? [];
    return schemaInterfaces.includes(requiredLink.schemaInterface.id);
  }
  return false;
}

/**
 * Whether there are any valid entity link options for the given schema.
 * If no schema is currently selected, the user can select any entity link field first,
 * then select a compatible schema after.
 */
function hasCompatibleEntityLinkOptions(
  entityLinkFields: EntityLinkFields,
  tagSchemaId: string | null,
  interfaceIdsBySchemaId?: Record<string, Array<string>>
): boolean {
  if (entityLinkFields.length === 0) {
    return false;
  }
  if (tagSchemaId == null) {
    return true;
  }
  assert(interfaceIdsBySchemaId != null, 'Interface IDs by schema ID is required');
  return entityLinkFields.some((entityLinkField) =>
    isSchemaValidForEntityLinkField(tagSchemaId, entityLinkField.field, interfaceIdsBySchemaId)
  );
}

/**
 * Array of entity link fields available for selection, along with their current state.
 * Each entry indicates whether the field already has a value set on the entity.
 */
export type EntityLinkFields = Array<{
  field: BioentityLinkFieldWithTargetSchema;
  hasExistingValue: boolean;
}>;

/**
 * Base props for the CreateLinkableBioentityForm component.
 * These are the common props regardless of whether entity link fields are provided.
 */
interface BaseCreateLinkableBioentityFormProps {
  // State values (controlled)
  name: string;
  folderId: Optional<string>;
  tagSchemaId: string | null;
  selectedStudyItems: Array<StudySearchItem>;

  // Change handlers
  onNameChange: (name: string) => void;
  onFolderIdChange: (folderId: string) => void;
  onTagSchemaIdChange: (tagSchemaId: string | null) => void;
  onSelectedStudyItemsChange: (studyItems: Array<StudySearchItem>) => void;
  onEntityLinkFieldChange: (selectedEntityLinkField: EntityLinkFieldOrDummy) => void;
  oligoAwareFolderItemTypes: Array<OligoAwareFolderItemType>;
  entityLinkFieldLabel?: string;
}

/**
 * Props for the CreateLinkableBioentityForm component.
 * Discriminated union ensures that if entity link fields are provided,
 * both selectedEntityLinkField and entityLinkFields must be present.
 */
export type CreateLinkableBioentityFormProps = BaseCreateLinkableBioentityFormProps &
  (
    | {
        selectedEntityLinkField: EntityLinkFieldOrDummy;
        entityLinkFields: EntityLinkFields;
      }
    | {
        selectedEntityLinkField: undefined;
        entityLinkFields: undefined;
      }
  );

/**
 * Validates the form state and returns whether the form can be submitted.
 * Also provides error messages for specific validation failures.
 *
 * @returns Object containing validation state and error messages
 */
export function getCreateFormValidation({
  name,
  folderId,
  selectedEntityLinkField,
  entityLinkFields,
  tagSchemaId,
  interfaceIdsBySchemaId,
}: {
  name: string;
  folderId: Optional<string>;
  selectedEntityLinkField?: EntityLinkFieldOrDummy;
  entityLinkFields?: EntityLinkFields;
  tagSchemaId: string | null;
  interfaceIdsBySchemaId?: Record<string, Array<string>>;
}): {isValid: boolean; nameIsTooLong: boolean; nameErrorMessage?: string} {
  const nameOrFolderMissing = !folderId || name.trim().length === 0;
  const nameIsTooLong = name.length > MAX_NAME_LENGTH;
  const additionalSubmitDisabled =
    selectedEntityLinkField != null &&
    ((hasCompatibleEntityLinkOptions(entityLinkFields ?? [], tagSchemaId, interfaceIdsBySchemaId) &&
      selectedEntityLinkField === UNSELECTED_DUMMY_OBJECT) ||
      (isSchemaInterfaceLinkField(selectedEntityLinkField) && tagSchemaId == null));

  const nameErrorMessage = nameIsTooLong
    ? `Names can be at most ${MAX_NAME_LENGTH} characters long (currently ${name.length})`
    : undefined;

  return {
    isValid: !nameOrFolderMissing && !additionalSubmitDisabled && !nameIsTooLong,
    nameIsTooLong,
    nameErrorMessage,
  };
}

/**
 * A controlled form component for creating bioentities (e.g., AA sequences, DNA sequences).
 *
 * This form provides fields for:
 * - Entity name (required)
 * - Folder/project location (required)
 * - Optional studies association
 * - Optional entity link field selection (with schema/interface compatibility validation)
 * - Optional schema selection (automatically set when a schema-specific link field is chosen)
 *
 * The form handles complex validation logic around entity link fields and schemas,
 * ensuring that selected fields are compatible with the chosen schema and that
 * schema interface link fields have a schema selected.
 *
 * @example
 * ```tsx
 * <CreateLinkableBioentityForm
 *   name={name}
 *   folderId={folderId}
 *   tagSchemaId={tagSchemaId}
 *   selectedStudyItems={[]}
 *   onNameChange={setName}
 *   onFolderIdChange={setFolderId}
 *   onTagSchemaIdChange={setTagSchemaId}
 *   onEntityLinkFieldChange={noop}
 *   onSelectedStudyItemsChange={noop}
 *   oligoAwareFolderItemTypes={[ModelTypeConstants.PROTEIN]}
 * />
 * ```
 */
export default function CreateLinkableBioentityForm({
  name,
  folderId,
  tagSchemaId,
  selectedEntityLinkField,
  selectedStudyItems,
  onNameChange,
  onFolderIdChange,
  onTagSchemaIdChange,
  onEntityLinkFieldChange,
  onSelectedStudyItemsChange,
  entityLinkFields = [],
  oligoAwareFolderItemTypes,
  entityLinkFieldLabel,
}: CreateLinkableBioentityFormProps): JSX.Element {
  const {interfaceIdsBySchemaId} = useSchemaSSIsByIdQuery();

  const {nameErrorMessage} = getCreateFormValidation({
    name,
    folderId,
    selectedEntityLinkField,
    entityLinkFields,
    tagSchemaId,
    interfaceIdsBySchemaId,
  });

  const handleSetFolderId = (e: {target: {value: string}}): void => {
    onFolderIdChange(e.target.value);
  };

  const handleChangeEntityLinkField = (field: EntityLinkFieldOrDummy): void => {
    onEntityLinkFieldChange(field);
    if (isSchemaLinkField(field)) {
      // Entity links always link to a specific schema or SSI.
      onTagSchemaIdChange(field.requiredLink.tagSchema!.id);
    }
  };

  const getEntityLinkFieldOptions = (): Array<{
    key: number | string;
    name: string;
    handleOnClick: () => void;
    disabled?: boolean;
  }> => [
    {
      key: 'None',
      name: 'None',
      handleOnClick: () => {
        handleChangeEntityLinkField(SELECT_NO_FIELD_DUMMY_OBJECT);
      },
    },
    ...entityLinkFields
      .filter(
        (entityLinkField) =>
          tagSchemaId == null ||
          isSchemaValidForEntityLinkField(tagSchemaId, entityLinkField.field, interfaceIdsBySchemaId)
      )
      .map(({field, hasExistingValue}) => {
        if (!field.isMulti && hasExistingValue) {
          return {
            key: field.id,
            disabled: true,
            name: `${field.displayName} (Already specified)`,
            handleOnClick: noop,
          };
        } else {
          return {
            key: field.id,
            name: field.displayName,
            handleOnClick: () => {
              handleChangeEntityLinkField(field);
            },
          };
        }
      }),
  ];

  return (
    <Stack spacing="0">
      <FormGroup>
        <FieldLabel label="Entity name" isRequired={true} htmlFor="name" />
        <TextInput
          id="name"
          autoFocus={true}
          placeholder="Name"
          onChange={(e) => {
            onNameChange(e.target.value);
          }}
          value={name}
          error={nameErrorMessage}
          onFocus={(e) => {
            const input = e.target;
            input.setSelectionRange(0, input.value.length);
          }}
          {...getTestProps({component: 'CreateLinkableBioentityForm', element: 'nameInput'})}
        />
      </FormGroup>

      <Group spacing="md" align="flex-start" grow={true}>
        <FormGroup>
          <div>
            <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={true} />
            <FolderSelector
              buttonSize={ButtonSize.MEDIUM}
              enableRedesign={true}
              showInventory={true}
              value={folderId}
              onChange={handleSetFolderId}
              unsetOnFetchFail={true}
              allowNone={false}
            />
          </div>
        </FormGroup>
        {BenchlingConfig.STUDIES_ENABLE && (
          <FormGroup>
            <div>
              <FieldLabel label="Studies" />
              <StudySearchInput
                selectedItems={selectedStudyItems}
                shouldAllowMultiple={true}
                onChange={onSelectedStudyItemsChange}
              />
            </div>
          </FormGroup>
        )}
      </Group>

      {entityLinkFields.length > 0 && (
        <FormGroup>
          <FieldLabel label={entityLinkFieldLabel} />
          <SelectDropdown
            disabled={!hasCompatibleEntityLinkOptions(entityLinkFields, tagSchemaId, interfaceIdsBySchemaId)}
            displayName={
              hasCompatibleEntityLinkOptions(entityLinkFields, tagSchemaId, interfaceIdsBySchemaId)
                ? // Typescript doesn't know that selectedEntityLinkField must be defined if entityLinkFields is defined
                  selectedEntityLinkField!.displayName
                : 'No compatible fields available'
            }
            menu={
              <Menu width={MenuWidth.FULL}>
                {getEntityLinkFieldOptions().map(({key, name: optionName, handleOnClick, disabled}) => (
                  <Option key={key} isDisabled={disabled} onSelect={handleOnClick}>
                    {optionName}
                  </Option>
                ))}
              </Menu>
            }
            dataTestAttrs={{component: 'CreateLinkableBioentityForm', element: 'entityLinkFieldDropdown'}}
          />
        </FormGroup>
      )}
      {isFeatureEnabled({feature: RegistryFeature.REGISTRY_SET_SCHEMA}) && (
        <FormGroup>
          <FieldLabel label="Schema" />
          <TagSchemaSelector
            allowNone={!isSelectedEntityLinkField(selectedEntityLinkField)}
            isDisabled={isSchemaLinkField(selectedEntityLinkField)}
            oligoAwareFolderItemTypes={oligoAwareFolderItemTypes}
            requiredPermissions={[OBJECTS_CREATABLE_PERMISSION]}
            selectedTagSchemaId={tagSchemaId}
            tagSchemaFilterFn={(schema) => {
              if (!isSelectedEntityLinkField(selectedEntityLinkField)) {
                return true;
              }
              return isSchemaValidForEntityLinkField(
                schema.id,
                selectedEntityLinkField,
                interfaceIdsBySchemaId
              );
            }}
            onChange={(tagSchema) => {
              onTagSchemaIdChange(tagSchema ? tagSchema.id : null);
            }}
          />
        </FormGroup>
      )}
    </Stack>
  );
}
